import React, { useContext } from "react"
import { MdClose, MdSettings } from "react-icons/md"
import {
	MobergButton,
	MobergButtonShape,
	MobergButtonVariant,
	MobergCheckbox,
	MobergColumn,
	MobergDropdown,
	MobergIconSize,
	MobergInputLabel,
	MobergNumberInput,
	MobergRow,
	MobergShortTextInput,
	MobergTheme,
} from "../../../../../Moberg"
import { ModalityConfigurationDiv } from "../../../../../Constants/StyledComponents"
import { ColorSpectrum, HEATMAP_COLOR_SPECTRUM_OPTIONS } from "../../../../../Pages/Data/Visualize/DataReview/Types/ColorSpectrum"
import { HeatmapTraceConfigJSON, RenderStrategy, TraceConfigJSON, TraceOption } from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { SpectrumSample } from "../SpectrumSample"
import { EditAnalysisModal } from "../../../../../Pages/Data/Visualize/DataReview/Components/Modals/EditAnalysis/EditAnalysisModal"
import { useModalProvider } from "../../../../../Providers/ModalProvider"
import { getAnalyticDisplayName, OnDemandAnalysis } from "../../../../../Pages/Data/Visualize/DataReview/Types/AnalysisDetails"
import { HeatmapGraphModalitiesContext } from "./HeatmapGraphModalities"
import { SourceDataOriginSummary } from "../SourceDataOriginSummary"
import { useRecoilValue } from "recoil"
import { currentPatientFileInfoAtom } from "../../../../../Pages/Data/Visualize/DataReview/Atoms/PatientFile"

export const SourceHeatmapModalities: React.FC = () => {
	const { sourceTraceOptions, reasonAddMeasurementIsDisabled, updateGraphProperty } = useContext(ConfigureWindowModalContext)
	const { activeSourceTraces, handleDelete, handleColorSpectrumChange, handleValidationErrorChange, updateTrace } = useContext(HeatmapGraphModalitiesContext)
	const { createModal } = useModalProvider()
	const { sources } = useRecoilValue(currentPatientFileInfoAtom)
	const heatmapTraceOptions = sourceTraceOptions.filter((option: TraceOption) => option.supportedRenderStrategies.includes(RenderStrategy.HEATMAP))

	const addModality = () => {
		const newTrace: HeatmapTraceConfigJSON = {
			...heatmapTraceOptions[0].value,
			id: `trace-${new Date(Date.now()).toISOString()}`,
			renderStrategy: RenderStrategy.HEATMAP,
			lowerBound: 0,
			upperBound: 100,
			colorSpectrum: ColorSpectrum.INFERNO,
			isVisible: true,
		}

		updateGraphProperty("traces", (previous: TraceConfigJSON[]) => [...previous, newTrace])
		updateTrace(newTrace.id, newTrace, { useModalitySpecificDefaults: true })
	}

	function traceAnalysisArgumentsChanged(trace: HeatmapTraceConfigJSON, newArgs: OnDemandAnalysis | undefined) {
		updateTrace(trace.id, {
			...trace,
			onDemandAnalysis: newArgs,
			dataKey: newArgs
				? `${trace.rawDataKey}-${JSON.stringify(newArgs)}` // we have to be able to uniquely identify analytic traces by the same name, but with different calculations.
				: trace.rawDataKey,
		})
	}

	return (
		<ModalityConfigurationDiv>
		<MobergColumn gap="16px">
			<SourceDataOriginSummary />
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "auto auto auto auto auto auto auto auto auto",
					columnGap: "16px",
					rowGap: "8px",
					padding: "0 16px",
					maxWidth: "fit-content",
				}}
			>
				<MobergInputLabel text={"Source"} />
				<MobergInputLabel text={"Label"} />
				<MobergInputLabel text={"Color spectrum"} />
				<MobergInputLabel text={"Lower bound"} />
				<MobergInputLabel text={""} />
				<MobergInputLabel text={"Upper bound"} />
				<MobergInputLabel text={"Post processing"} style={{ whiteSpace: "nowrap" }} />
				<MobergInputLabel text={"Visible"} />
				<MobergInputLabel text={""} />

				{activeSourceTraces.map(trace => {
					const sourceIsReadOnly = sources.get(trace.dataSource)?.read_only ?? true
					return (
						<React.Fragment key={trace.id}>
							<MobergDropdown
								key={`${trace.id}-modalitySelector`}
								options={heatmapTraceOptions}
								onChange={newTrace => updateTrace(trace.id, newTrace, { useModalitySpecificDefaults: true })}
								selectedValue={trace}
								equals={(a, b) => a.rawDataKey === b.rawDataKey && a.dataSource === b.dataSource}
								width={224}
							/>

							<MobergShortTextInput
								limit={20}
								defaultValue={trace.name}
								value={trace.name}
								onChange={(name: string) => updateTrace(trace.id, { ...trace, name })}
								style={{ width: "150px" }}
							/>

							<MobergDropdown
								key={`${trace.id}-spectrumSelector`}
								selectedValue={trace.colorSpectrum}
								onChange={spectrum => handleColorSpectrumChange(spectrum, trace)}
								options={HEATMAP_COLOR_SPECTRUM_OPTIONS}
								width={175}
							/>

							<MobergNumberInput
								key={`${trace.id}-lowerBound`}
								defaultValue={trace.lowerBound}
								value={trace.lowerBound}
								onChange={lowerBound => updateTrace(trace.id, { ...trace, lowerBound: lowerBound ?? 0 })}
								onValidationErrorChange={error => handleValidationErrorChange(`${trace.id}-lowerBound`, error)}
							/>

							<MobergRow key={`${trace.id}-spectrumSample`}>
								<SpectrumSample colorSpectrum={trace.colorSpectrum} />
							</MobergRow>

							<MobergNumberInput
								key={`${trace.id}-upperBound`}
								defaultValue={trace.upperBound}
								value={trace.upperBound}
								onChange={upperBound => updateTrace(trace.id, { ...trace, upperBound: upperBound ?? 0 })}
								onValidationErrorChange={error => handleValidationErrorChange(`${trace.id}-upperBound`, error)}
							/>

							<MobergRow gap="16px">
								<MobergButton
									tooltip={sourceIsReadOnly ? "Post processing is not available for this time-series source." : "Edit the post processing of " + trace.dataKey}
									shape={MobergButtonShape.SQUARE}
									disabled={sourceIsReadOnly}
									onClick={() => {
										createModal(
											<EditAnalysisModal
												analysis={trace.onDemandAnalysis}
												rawDataKey={trace.rawDataKey}
												onChange={(newArgs: OnDemandAnalysis | undefined) => traceAnalysisArgumentsChanged(trace, newArgs)}
											/>,
										)
									}}
								>
									<MdSettings size={MobergIconSize.REGULAR} />
								</MobergButton>

								<span style={{ color: "var(--color-text-primary)" }}>
									{trace.onDemandAnalysis ? `(${getAnalyticDisplayName(trace.onDemandAnalysis?.analytic)})` : "(none)"}
								</span>
							</MobergRow>

							<MobergCheckbox isChecked={trace.isVisible ?? true} onChange={isVisible => updateTrace(trace.id, { ...trace, isVisible })} />

							<MobergButton key={`${trace.id}-deleteHeatmap`} shape={MobergButtonShape.SQUARE} onClick={() => handleDelete(trace.id)} style={{ padding: "6px" }}>
								<MdClose size={MobergIconSize.REGULAR} />
							</MobergButton>
						</React.Fragment>
					)
				})}

				<MobergButton
					onClick={addModality}
					theme={MobergTheme.BLUE}
					variant={MobergButtonVariant.OUTLINE}
					style={{ width: "175px" }}
					disabled={!!reasonAddMeasurementIsDisabled || heatmapTraceOptions.length === 0}
					tooltip={reasonAddMeasurementIsDisabled}
				>
					Add source data
				</MobergButton>
			</div>
		</MobergColumn>
		</ModalityConfigurationDiv>
	)
}
